// Client-side ID card reader — PAN and Aadhar.
// Runs Tesseract (WASM, in a web worker) over an uploaded card and pulls out
// the holder's name and card number. Nothing leaves the browser.
//
// A phone photo of an ID card is a hostile input: it is often rotated, tilted,
// shot against a hologram, and printed in two columns — Hindi/Malayalam beside
// English — which the English model reads as convincing-looking Latin junk. So
// we brute-force the four right-angle rotations, score each pass, read word
// boxes rather than flat text, and escalate to a binarised sparse-text pass
// when the first sweep comes back incomplete.
import * as pdfjsLib from "pdfjs-dist/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

// Tesseract assets come from the CDN by default. Set REACT_APP_TESSERACT_PATH
// (e.g. "/tesseract") to serve worker/core/traineddata from your own origin.
const ASSET_BASE = process.env.REACT_APP_TESSERACT_PATH || "";
const ROTATIONS = [0, 270, 90, 180]; // upright first, then the common phone tilts
// Word boxes + confidences are what let us tell card print from OCR noise.
const RECOGNIZE_OUTPUT = { text: true, blocks: true };
const TARGET_LONG_EDGE = 1800; // px — enough detail for 12pt card print
const WORKER_IDLE_MS = 120000;

/* ── File → canvas ──────────────────────────────────────────────────────── */

export const canReadDocument = (file) => {
  if (!file) return false;
  const name = (file.name || "").toLowerCase();
  return (
    (file.type || "").startsWith("image/") ||
    file.type === "application/pdf" ||
    /\.(jpe?g|png|webp|bmp|gif|pdf)$/.test(name)
  );
};

const isPdf = (file) =>
  file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");

const loadImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      /* Safari/older browsers — fall through to <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Could not decode the image"));
      img.src = url;
    });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
};

const renderPdfFirstPage = async (file) => {
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(1);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({
    scale: Math.min(3, Math.max(1, TARGET_LONG_EDGE / base.width)),
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  await page.render({ canvasContext: canvas.getContext("2d"), viewport })
    .promise;
  return canvas;
};

/* ── Pre-processing ─────────────────────────────────────────────────────── */

// Grayscale + percentile contrast stretch (and optionally an Otsu threshold),
// which is what lifts dark print off a tinted, laminated card.
const enhance = (ctx, w, h, binarize) => {
  const img = ctx.getImageData(0, 0, w, h);
  const px = img.data;
  const hist = new Uint32Array(256);

  for (let i = 0; i < px.length; i += 4) {
    const g = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
    px[i] = px[i + 1] = px[i + 2] = g;
    hist[g | 0]++;
  }

  const total = w * h;
  let lo = 0;
  let hi = 255;
  for (let seen = 0, v = 0; v < 256; v++) {
    seen += hist[v];
    if (seen > total * 0.02) {
      lo = v;
      break;
    }
  }
  for (let seen = 0, v = 255; v >= 0; v--) {
    seen += hist[v];
    if (seen > total * 0.02) {
      hi = v;
      break;
    }
  }
  const span = Math.max(1, hi - lo);

  let cut = 128;
  if (binarize) {
    // Otsu over the stretched range.
    let sum = 0;
    for (let v = 0; v < 256; v++) sum += v * hist[v];
    let sumB = 0;
    let wB = 0;
    let best = -1;
    for (let v = 0; v < 256; v++) {
      wB += hist[v];
      if (!wB) continue;
      const wF = total - wB;
      if (!wF) break;
      sumB += v * hist[v];
      const between = wB * wF * Math.pow(sumB / wB - (sum - sumB) / wF, 2);
      if (between > best) {
        best = between;
        cut = v;
      }
    }
    cut = ((cut - lo) / span) * 255;
  }

  for (let i = 0; i < px.length; i += 4) {
    let g = ((px[i] - lo) / span) * 255;
    g = g < 0 ? 0 : g > 255 ? 255 : g;
    if (binarize) g = g < cut ? 0 : 255;
    px[i] = px[i + 1] = px[i + 2] = g;
  }
  ctx.putImageData(img, 0, 0);
};

const prepare = (source, degrees, binarize) => {
  const sw = source.width;
  const sh = source.height;
  const scale = Math.min(3, Math.max(0.35, TARGET_LONG_EDGE / Math.max(sw, sh)));
  const swapped = degrees % 180 !== 0;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round((swapped ? sh : sw) * scale);
  canvas.height = Math.round((swapped ? sw : sh) * scale);

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(
    source,
    (-sw * scale) / 2,
    (-sh * scale) / 2,
    sw * scale,
    sh * scale,
  );
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  enhance(ctx, canvas.width, canvas.height, binarize);
  return canvas;
};

/* ── Segments: card print, separated from OCR noise ─────────────────────── */

/* Both cards print two languages side by side. Tesseract stitches the columns
   into one "line", so `MOHAMMED ASHIQ M` arrives with Devanagari mis-read as
   Latin glued to its right. Word boxes tell the columns apart: split each line
   wherever the horizontal gap exceeds the text height, and drop words the
   engine had no confidence in — that junk is never real print. */
const MIN_WORD_CONFIDENCE = 20;
const DEFAULT_CONFIDENCE = 50; // assumed when parsing plain text

const segmentsFromBlocks = (blocks) => {
  const segments = [];
  const lines = [];
  (blocks || []).forEach((b) =>
    (b.paragraphs || []).forEach((p) => lines.push(...(p.lines || []))),
  );

  for (const line of lines) {
    const words = (line.words || []).filter(
      (w) => w.confidence >= MIN_WORD_CONFIDENCE && w.text && w.text.trim(),
    );
    if (!words.length) continue;

    const heights = words
      .map((w) => w.bbox.y1 - w.bbox.y0)
      .sort((a, b) => a - b);
    const maxGap = Math.max(24, heights[heights.length >> 1] * 1.4);

    let run = [];
    const flush = () => {
      if (!run.length) return;
      segments.push({
        text: run
          .map((w) => w.text)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim(),
        confidence: run.reduce((sum, w) => sum + w.confidence, 0) / run.length,
      });
      run = [];
    };
    for (const w of words) {
      const prev = run[run.length - 1];
      if (prev && w.bbox.x0 - prev.bbox.x1 > maxGap) flush();
      run.push(w);
    }
    flush();
  }
  return segments;
};

const segmentsFromText = (text) =>
  String(text)
    .split(/\r?\n/)
    .map((l) =>
      l
        .replace(/[^\x20-\x7E]/g, " ")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .filter(Boolean)
    .map((t) => ({ text: t, confidence: DEFAULT_CONFIDENCE }));

/* ── Names ──────────────────────────────────────────────────────────────── */

const NOISE_WORDS =
  /\b(INCOME|TAX|DEPARTMENT|DEPT|GOVT|GOVERNMENT|INDIA|PERMANENT|ACCOUNT|NUMBER|CARD|SIGNATURE|SIGN|DATE|BIRTH|DOB|YEAR|FATHER|MOTHER|HUSBAND|NAME|AADHAAR|AADHAR|UNIQUE|IDENTIFICATION|AUTHORITY|UIDAI|MALE|FEMALE|TRANSGENDER|ADDRESS|ENROL+MENT|MOBILE|VID|HELP|WWW|DOWNLOAD|ISSUE)\b/i;

const looksLikeName = (line) => {
  const s = line.trim();
  if (s.length < 4 || s.length > 48) return false;
  if (!/^[A-Za-z][A-Za-z.'\s-]*$/.test(s)) return false; // letters only, no digits
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length > 5) return false;
  if (NOISE_WORDS.test(s)) return false;
  const letters = s.replace(/[^A-Za-z]/g, "");
  if (letters.length < 4) return false;
  // Card print is either all caps (PAN) or title case (Aadhar). Anything else
  // — "LeyHeEy", "gEATeR" — is the other script being mis-read as Latin.
  if (letters === letters.toUpperCase()) return true;
  return words.every((w) =>
    /^[A-Z][a-z]*([.'-][A-Za-z][a-z]*)*\.?$/.test(w),
  );
};

const NAME_LABEL = /\bname\b/i;
const RELATIVE_LABEL = /\b(father|husband|mother|[sdwc]\s*[/|]\s*o)\b/i;

// Longer, more confident text beats short OCR debris.
const rank = (seg) =>
  seg.confidence + Math.min(20, seg.text.replace(/[^A-Za-z]/g, "").length);

/**
 * Pick the holder's name out of ordered card segments.
 * Layout is the real signal — on both cards the holder's name is printed
 * above the relative's name, so the father's half of the card is cut out of
 * scope entirely and cannot win even when it reads more cleanly.
 *
 * @param {{text: string, confidence: number}[]} segments in reading order
 * @param {{preferLast?: boolean}} opts unlabelled cards: Aadhar prints the name
 *   directly above the date of birth (last), PAN at the top of its block (first)
 */
export const pickName = (segments, { preferLast = false } = {}) => {
  const isRelative = (s) => RELATIVE_LABEL.test(s.text);
  const labelIdx = segments.findIndex(
    (s) => NAME_LABEL.test(s.text) && !isRelative(s),
  );
  const relativeIdx = segments.findIndex(isRelative);

  // The label sometimes carries its value inline ("Name: PRIYA SHARMA").
  if (labelIdx >= 0) {
    const inline = segments[labelIdx].text.replace(
      /^.*?\bname\b\s*[:/-]?\s*/i,
      "",
    );
    if (looksLikeName(inline)) return inline;
  }

  const start = labelIdx >= 0 ? labelIdx + 1 : 0;
  const end = relativeIdx >= start ? relativeIdx : segments.length;
  const window = segments.slice(start, end).filter((s) => looksLikeName(s.text));
  if (!window.length) return null;

  // Labelled card: take the strongest reading in the window.
  if (labelIdx >= 0)
    return window.reduce((a, b) => (rank(b) > rank(a) ? b : a)).text;
  return (preferLast ? window[window.length - 1] : window[0]).text;
};

const isAllCaps = (s) => {
  const letters = s.replace(/[^A-Za-z]/g, "");
  return letters.length > 0 && letters === letters.toUpperCase();
};

// Capitalise after spaces and after the separators that appear inside names
// ("ASHIQ.M" → "Ashiq.M", "D'SOUZA" → "D'Souza").
const toTitleCase = (value) =>
  value
    .toLowerCase()
    .replace(/(^|[\s.'-])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());

/** Cards printed in caps read better title-cased; anything already mixed-case
    is left exactly as printed. */
export const normalizeName = (value) =>
  !value ? null : isAllCaps(value) ? toTitleCase(value.trim()) : value.trim();

/* ── PAN ────────────────────────────────────────────────────────────────── */

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
// 4th character is the holder type; P = individual, and the rest are entities.
const HOLDER_TYPES = "ABCFGHJLPTKE";
const TO_LETTER = { 0: "O", 1: "I", 2: "Z", 4: "A", 5: "S", 6: "G", 8: "B" };
const TO_DIGIT = {
  O: "0",
  Q: "0",
  D: "0",
  I: "1",
  L: "1",
  Z: "2",
  A: "4",
  S: "5",
  G: "6",
  B: "8",
};

const coerceToPan = (raw) => {
  let out = "";
  for (let i = 0; i < 10; i++) {
    const ch = raw[i];
    if (i < 5 || i === 9) out += /[A-Z]/.test(ch) ? ch : TO_LETTER[ch] || ch;
    else out += /[0-9]/.test(ch) ? ch : TO_DIGIT[ch] || ch;
  }
  return out;
};

export const findPanNumber = (text) => {
  const tokens = String(text)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/);

  const candidates = [];
  const consider = (token) => {
    if (token.length !== 10) return;
    if (PAN_RE.test(token)) candidates.push({ pan: token, exact: true });
    else {
      const fixed = coerceToPan(token);
      if (PAN_RE.test(fixed)) candidates.push({ pan: fixed, exact: false });
    }
  };

  tokens.forEach(consider);
  // OCR sometimes glues the PAN to a neighbouring word — slide a window too.
  if (!candidates.length) {
    tokens
      .filter((t) => t.length > 10 && t.length <= 24)
      .forEach((t) => {
        for (let i = 0; i + 10 <= t.length; i++) consider(t.slice(i, i + 10));
      });
  }
  if (!candidates.length) return null;

  const score = (c) =>
    (c.exact ? 2 : 0) + (HOLDER_TYPES.includes(c.pan[3]) ? 1 : 0);
  candidates.sort((a, b) => score(b) - score(a));
  return candidates[0].pan;
};

/* ── Aadhar ─────────────────────────────────────────────────────────────── */

// Aadhar numbers carry a Verhoeff check digit. Enforcing it is what keeps a
// mis-read digit — or a stray 12-digit run — out of an HR record.
const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

export const isValidAadhaar = (digits) => {
  if (!/^[2-9][0-9]{11}$/.test(digits)) return false; // never starts 0 or 1
  let c = 0;
  const reversed = digits.split("").reverse();
  for (let i = 0; i < reversed.length; i++) {
    c = VERHOEFF_D[c][VERHOEFF_P[i % 8][Number(reversed[i])]];
  }
  return c === 0;
};

// Digits the engine may have read as letters, inside otherwise-numeric tokens.
const DIGIT_LOOKALIKES = {
  O: "0",
  D: "0",
  Q: "0",
  I: "1",
  L: "1",
  T: "1",
  Z: "2",
  A: "4",
  S: "5",
  G: "6",
  B: "8",
};

export const findAadhaarNumber = (text) => {
  const tokens = String(text)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((t) => {
      // Repair "778I" → "7781", but only where the token is mostly digits.
      const digits = (t.match(/[0-9]/g) || []).length;
      if (!digits || digits < t.length - 2) return t;
      return t.replace(/[A-Z]/g, (ch) => DIGIT_LOOKALIKES[ch] || ch);
    });

  const candidates = [];
  tokens.forEach((t, i) => {
    if (!/^[0-9]+$/.test(t)) return;
    // 12 digits printed as one run…
    if (t.length === 12) candidates.push(t);
    // …or as the usual 4-4-4 groups. Longer runs (VID is 16) are not Aadhar.
    if (t.length === 4) {
      const b = tokens[i + 1];
      const c = tokens[i + 2];
      if (/^[0-9]{4}$/.test(b || "") && /^[0-9]{4}$/.test(c || ""))
        candidates.push(t + b + c);
    }
  });

  const valid = candidates.find(isValidAadhaar);
  return valid ? `${valid.slice(0, 4)} ${valid.slice(4, 8)} ${valid.slice(8)}` : null;
};

// Aadhar has no "Name" label — the name is printed directly above the date of
// birth / gender / number block, so that block is the anchor we search up from.
const AADHAAR_ANCHOR =
  /\b(year of birth|date of birth|d\.?o\.?b|birth|male|female|transgender|vid)\b/i;

const findAadhaarName = (segments) => {
  const anchor = segments.findIndex(
    (s) => AADHAAR_ANCHOR.test(s.text) || findAadhaarNumber(s.text),
  );
  // No anchor means this is not the front of an Aadhar card (the back carries
  // only the address) — better an empty field than a line of address text.
  if (anchor <= 0) return null;
  return pickName(segments.slice(0, anchor), { preferLast: true });
};

/* ── Card profiles ──────────────────────────────────────────────────────── */

const PROFILES = {
  pan: {
    keywords: /(INCOME\s*TAX|PERMANENT\s*ACCOUNT|GOVT|DEPARTMENT|FATHER)/i,
    findNumber: findPanNumber,
    findName: (segments) => pickName(segments),
  },
  aadhaar: {
    keywords:
      /(GOVERNMENT\s*OF\s*INDIA|UNIQUE\s*IDENTIFICATION|AADHAAR|AADHAR|YEAR\s*OF\s*BIRTH|\bDOB\b|\bMALE\b|\bFEMALE\b)/i,
    findNumber: findAadhaarNumber,
    findName: findAadhaarName,
  },
};

const parse = (data, profile) => {
  const text = data.text || "";
  const blockSegments = segmentsFromBlocks(data.blocks);
  // Word geometry when the engine gave us any; flat text otherwise.
  const segments = blockSegments.length ? blockSegments : segmentsFromText(text);

  const number = profile.findNumber(text);
  let name = profile.findName(segments);
  const keywordHits = (text.match(profile.keywords) || []).length;
  // A wrongly-rotated pass reads holograms as capital letters and can hand back
  // a plausible-looking "name". Only trust one from a page that also reads as
  // the right kind of card — better an empty field than a wrong one.
  if (!number && !keywordHits) name = null;

  return {
    number,
    name,
    score: (number ? 3 : 0) + (name ? 3 : 0) + keywordHits,
  };
};

/* ── Tesseract worker (lazy, shared, self-retiring) ─────────────────────── */

let workerPromise = null;
let retireTimer = null;

const getWorker = async () => {
  clearTimeout(retireTimer);
  if (!workerPromise) {
    workerPromise = import("tesseract.js").then(({ createWorker }) =>
      createWorker("eng", 1, {
        ...(ASSET_BASE
          ? {
              workerPath: `${ASSET_BASE}/worker.min.js`,
              corePath: `${ASSET_BASE}/`,
              langPath: ASSET_BASE,
            }
          : {}),
      }),
    );
  }
  return workerPromise;
};

const retireWorkerSoon = () => {
  clearTimeout(retireTimer);
  const pending = workerPromise;
  retireTimer = setTimeout(async () => {
    if (workerPromise !== pending) return;
    workerPromise = null;
    try {
      (await pending)?.terminate();
    } catch {
      /* already gone */
    }
  }, WORKER_IDLE_MS);
};

/* ── Public API ─────────────────────────────────────────────────────────── */

/**
 * Read an ID card image (or single-page PDF) and return what it could find.
 * Never throws for "unreadable" — it just returns empty fields.
 *
 * @param {File} file the uploaded card
 * @param {"pan"|"aadhaar"} kind which card to read
 * @returns {Promise<{name: string|null, number: string|null, text: string}>}
 */
export async function extractCardDetails(file, kind, { onProgress } = {}) {
  const profile = PROFILES[kind];
  if (!profile) throw new Error(`Unknown card type: ${kind}`);
  if (!canReadDocument(file)) return { name: null, number: null, text: "" };

  const tick = (v) => onProgress?.(Math.min(0.99, Math.max(0, v)));
  tick(0.02);

  const source = isPdf(file)
    ? await renderPdfFirstPage(file)
    : await loadImage(file);
  const worker = await getWorker();
  tick(0.12);

  let best = { score: 0, number: null, name: null, rotation: 0, text: "" };
  try {
    for (let i = 0; i < ROTATIONS.length; i++) {
      const canvas = prepare(source, ROTATIONS[i], false);
      const { data } = await worker.recognize(canvas, {}, RECOGNIZE_OUTPUT);
      const found = parse(data, profile);
      if (found.score > best.score) {
        best = { ...found, rotation: ROTATIONS[i], text: data.text || "" };
      }
      tick(0.12 + ((i + 1) / ROTATIONS.length) * 0.68);
      if (best.number && best.name) break; // confident enough — stop early
    }

    // Escalation: sparse-text over a binarised image at the best rotation.
    if (!best.number || !best.name) {
      const { PSM } = await import("tesseract.js");
      await worker.setParameters({ tessedit_pageseg_mode: PSM.SPARSE_TEXT });
      try {
        const canvas = prepare(source, best.rotation, true);
        const { data } = await worker.recognize(canvas, {}, RECOGNIZE_OUTPUT);
        const found = parse(data, profile);
        best = {
          ...best,
          number: best.number || found.number,
          name: best.name || found.name,
          text: `${best.text}\n${data.text || ""}`,
        };
      } finally {
        await worker.setParameters({ tessedit_pageseg_mode: PSM.AUTO });
      }
    }
  } finally {
    source.close?.();
    retireWorkerSoon();
    tick(1);
  }

  return {
    name: normalizeName(best.name),
    number: best.number || null,
    text: best.text,
  };
}
